import { useState, useEffect, useRef, ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useUser } from 'context/UserContext'
import { useKeyPress } from 'hooks/useKeyPress'
import { ContinueButton } from 'components/ContinueButton'
import { Heading } from 'components/Heading'
import Input from 'components/Input'
import { UnitSwitcher } from 'components/UnitSwitcher'

interface Props {
  nextRoute: string
}

export const Weight: React.FC<Props> = ({ nextRoute }) => {
  const { t } = useTranslation('translation', {
    useSuspense: false,
  })

  const METRIC_LBS_KEY = 1
  const METRIC_KG_KEY = 2
  const WEIGHT_UNITS = [
    { key: METRIC_LBS_KEY, unit: t('metric_lbs') },
    { key: METRIC_KG_KEY, unit: t('metric_kg') },
  ]
  const { user, setUser } = useUser()
  const genderBasedBackground =
    user.gender === 'm'
      ? 'bg-weight-male bg-center'
      : 'bg-weight-female bg-center'
  const navigate = useNavigate()
  const [canContinue, setCanContinue] = useState(false)
  const [weight, setWeight] = useState('')
  const [weightType, setWeightType] = useState(METRIC_KG_KEY)
  const weightInput = useRef(null)

  const onContinue = () => {
    if (!canContinue) {
      return
    }

    setUser({
      ...user,
      weight,
      weightType: weightType === METRIC_LBS_KEY ? 'lbs' : 'kg',
    })
    setTimeout(() => {
      navigate(nextRoute)
    }, 100)
  }

  const onWeightChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value

    if (/(^$|^\d{1,3}$)/.test(input)) {
      setWeight(input)
    }
  }

  useEffect(() => {
    if (weight === '') {
      return
    }

    if (weightType === METRIC_LBS_KEY) {
      setWeight(`${Math.round(parseInt(weight, 10) * 2.205)}`)
    } else {
      setWeight(`${Math.round(parseInt(weight, 10) / 2.205)}`)
    }
  }, [weightType]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCanContinue(weight.length > 1)
  }, [weight])

  useKeyPress('ENTER', onContinue)

  return (
    <>
      <div
        className={`default-container ${genderBasedBackground} flex flex-col`}>
        <div className="flex h-full w-full flex-col justify-between self-center overflow-auto px-7 pt-7 lg:w-5/6 xl:w-5/12">
          <Heading text={t('weight.headline')} />

          <div className="z-0 mx-auto mt-5 mb-28 w-full">
            <UnitSwitcher
              units={WEIGHT_UNITS}
              selected={weightType}
              onUnitSelected={(unit) => setWeightType(unit)}
            />
            <Input
              placeholder={weightType === METRIC_KG_KEY ? '70' : '115'}
              value={weight}
              id="weight_input"
              onChange={onWeightChange}
              ref={weightInput}
            />
          </div>
        </div>
        <ContinueButton onClick={onContinue} disabled={!canContinue} />
      </div>
    </>
  )
}
